import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import FoodCard from '../../components/FoodCard';
import { useRecommendations } from '../../hooks/useRecommendations';
import { useLocation } from '../../contexts/LocationContext';

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
    <div className="aspect-video bg-gray-200" />
    <div className="p-4 space-y-2.5">
      <div className="h-5 bg-gray-200 rounded-xl w-3/4" />
      <div className="h-3 bg-gray-200 rounded-xl w-1/2" />
      <div className="h-3 bg-gray-200 rounded-xl w-full" />
      <div className="h-8 bg-gray-200 rounded-2xl w-full mt-2" />
    </div>
  </div>
);

const SurpriseMe = ({ metrics, onMetricRefresh }) => {
  const { locationString } = useLocation();

  const { recommendations, aiInsight, loading, aiUpdating, isQuick, error, refetch } = useRecommendations({
    comfort:  'dine-out',
    vibe:     'surprise-me',
    cuisine:  'all',
    dietary:  'all',
    location: null,
    deliveryOptions: ['dine-in', 'pickup'],
    easeOfAccess: [],
  });

  const [similarFilter, setSimilarFilter] = useState(null);

  const filteredRecommendations = useMemo(() => {
    if (!similarFilter) return recommendations;
    const { cuisine, vibe } = similarFilter;
    let filtered = recommendations;
    if (cuisine) {
      filtered = filtered.filter((item) => (item.cuisine || '').toLowerCase() === cuisine.toLowerCase());
    }
    if (filtered.length === 0 && vibe) {
      filtered = recommendations.filter((item) => (item.vibe || '').toLowerCase() === vibe.toLowerCase());
    }
    return filtered.length > 0 ? filtered : recommendations;
  }, [recommendations, similarFilter]);

  useEffect(() => {
    setSimilarFilter(null);
  }, [recommendations]);

  useEffect(() => {
    const handleRefresh = () => {
      setSimilarFilter(null);
      refetch({
        comfort: 'dine-out',
        vibe: 'surprise-me',
        cuisine: 'all',
        dietary: 'all',
        location: locationString || 'your city',
        deliveryOptions: ['dine-in', 'pickup'],
        easeOfAccess: [],
      });
    };
    window.addEventListener('surprise:refresh', handleRefresh);
    return () => window.removeEventListener('surprise:refresh', handleRefresh);
  }, [refetch, locationString]);

  useEffect(() => {
    const handleSimilar = (event) => {
      const { cuisine, vibe } = event.detail || {};
      if (!cuisine && !vibe) return;
      setSimilarFilter({ cuisine, vibe });
    };
    window.addEventListener('surprise:similar', handleSimilar);
    return () => window.removeEventListener('surprise:similar', handleSimilar);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#FF6B35] via-[#F4C430] to-[#FF8C42] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </Link>
          <Sparkles className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-2">Feeling Adventurous?</h1>
          <p className="text-white/90 text-lg mb-6">
            Let AI find your next favourite meal — no choices, just vibes.
          </p>

          {isQuick && aiUpdating && (
            <div data-testid="quick-recommendations-banner" className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 mb-6 max-w-lg mx-auto">
              <p className="text-white/90 text-sm">Instant picks loaded — personalizing with AI…</p>
            </div>
          )}

          {aiInsight && !loading && !aiUpdating && (
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 mb-6 max-w-lg mx-auto">
              <p className="text-white italic text-sm">"{aiInsight}"</p>
            </div>
          )}

          <Button
            data-testid="surprise-me-btn"
            onClick={() => {
              setSimilarFilter(null);
              refetch({ comfort: 'dine-out', vibe: 'surprise-me', cuisine: 'all', dietary: 'all', location: locationString || 'your city', deliveryOptions: ['dine-in', 'pickup'], easeOfAccess: [] });
            }}
            disabled={loading || aiUpdating}
            className="bg-white text-[#FF6B35] hover:bg-white/90 font-semibold px-6 py-3 h-auto rounded-2xl"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Generating...' : 'Surprise Me Again'}
          </Button>
        </div>
      </div>

      {/* Cards */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Your Surprise Lineup</h2>
        <p className="text-sm text-gray-500 text-center mb-6">
          Each card shows its recommendation type so you know what you're getting.
        </p>

        {error && (
          <div className="mb-6 p-3 rounded-2xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm max-w-lg mx-auto">
            <AlertCircle className="w-4 h-4" />{error} —{' '}
            <button onClick={() => { setSimilarFilter(null); refetch({ comfort: 'dine-out', vibe: 'surprise-me', cuisine: 'all', dietary: 'all', deliveryOptions: ['dine-in', 'pickup'], easeOfAccess: [] }); }} className="underline">
              Retry
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {loading && filteredRecommendations.length === 0
            ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
            : filteredRecommendations.slice(0, 3).map((dish) => (
                <FoodCard variant="recommendation"
                  key={dish.id}
                  dish={dish}
                  mode="dine-out"
                />
              ))}
        </div>
      </div>
    </div>
  );
};

export default SurpriseMe;
